import { useEffect, useState } from "react";
import { X, Pencil, Loader2 } from "lucide-react";

import { updateWorkspace } from "../services/workspaceService";
import { getEmployees } from "../services/employeeService";

const EditWorkspaceDrawer = ({
  open,
  onClose,
  workspace,
  project,
  onUpdated,
}) => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    workspace_code: "",
    workspace_name: "",
    execution_mode: "Work",
    workspace_lead_employee_uuid: "",
    estimated_hours: "",
    status: "Planning",
    display_order: 1,
    description: "",
  });

  /*
   * ============================================================
   * GET PROJECT UUID
   * ============================================================
   */
  const projectUuid =
    project?.uuid ||
    project?.project_uuid ||
    project?.projectUuid;

  /*
   * ============================================================
   * GET WORKSPACE UUID
   * ============================================================
   */
  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  /*
   * ============================================================
   * LOAD WORKSPACE DATA INTO FORM
   * ============================================================
   */
  useEffect(() => {
    if (!workspace) return;

    setFormData({
      workspace_code:
        workspace.workspace_code ||
        workspace.code ||
        "",

      workspace_name:
        workspace.workspace_name ||
        workspace.name ||
        "",

      execution_mode:
        workspace.execution_mode ||
        "Work",

      workspace_lead_employee_uuid:
        workspace.workspace_lead_employee_uuid ||
        "",

      estimated_hours:
        workspace.estimated_hours ??
        "",

      status:
        workspace.status ||
        "Planning",

      display_order:
        workspace.display_order ??
        1,

      description:
        workspace.description ||
        "",
    });
  }, [workspace]);

  /*
   * ============================================================
   * FETCH EMPLOYEES
   * ============================================================
   */
  useEffect(() => {
    if (!open) return;

    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        setError("");

        console.log(
          "========== FETCH EMPLOYEES =========="
        );

        const response = await getEmployees();

        console.log(
          "EMPLOYEE API RESPONSE:",
          response
        );

        /*
         * Support different API response structures
         */
        const employeeList =
          Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        console.log(
          "EMPLOYEE LIST:",
          employeeList
        );

        setEmployees(employeeList);
      } catch (error) {
        console.error(
          "FETCH EMPLOYEES ERROR:",
          error
        );

        setEmployees([]);

        setError(
          error.response?.data?.message ||
            "Failed to load employees."
        );
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [open]);

  /*
   * ============================================================
   * HANDLE INPUT CHANGE
   * ============================================================
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
   * ============================================================
   * UPDATE WORKSPACE
   * ============================================================
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectUuid) {
      setError("Project UUID is required.");
      return;
    }

    if (!workspaceUuid) {
      setError("Workspace UUID is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const updatedWorkspace = {
        workspace_code:
          formData.workspace_code,

        workspace_name:
          formData.workspace_name,

        execution_mode:
          formData.execution_mode,

        workspace_lead_employee_uuid:
          formData.workspace_lead_employee_uuid,

        estimated_hours:
          Number(formData.estimated_hours),

        status:
          formData.status,

        display_order:
          Number(formData.display_order),

        description:
          formData.description,
      };

      console.log(
        "========== UPDATE WORKSPACE =========="
      );

      console.log(
        "PROJECT UUID:",
        projectUuid
      );

      console.log(
        "WORKSPACE UUID:",
        workspaceUuid
      );

      console.log(
        "UPDATE BODY:",
        updatedWorkspace
      );

      const response =
        await updateWorkspace(
          projectUuid,
          workspaceUuid,
          updatedWorkspace
        );

      console.log(
        "UPDATE WORKSPACE RESPONSE:",
        response
      );

      /*
       * Send updated data to parent so UI
       * changes immediately.
       */
      if (onUpdated) {
        const updatedData =
          response?.data ||
          response;

        onUpdated(updatedData);
      }

      onClose();
    } catch (error) {
      console.error(
        "UPDATE WORKSPACE ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
          "Failed to update workspace."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open || !workspace) {
    return null;
  }

  return (
    <>
      {/* =====================================================
          BACKDROP
      ===================================================== */}

      <div
        className="fixed inset-0 z-[90] bg-black/40"
        onClick={onClose}
      />

      {/* =====================================================
          DRAWER
      ===================================================== */}

      <div className="fixed right-0 top-0 z-[100] flex h-screen w-full flex-col border-l border-slate-200 bg-white shadow-2xl md:w-[70%] lg:w-1/2">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="shrink-0 border-b border-slate-200 px-6 py-5">

          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-3">

                <Pencil
                  size={21}
                  className="text-blue-600"
                />

              </div>

              <div>

                <h2 className="text-xl font-bold text-slate-800">
                  Edit Workspace
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  {project?.projectName ||
                    project?.project_name ||
                    "Current Project"}
                </p>

              </div>

            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 transition hover:bg-slate-100"
            >
              <X size={20} />
            </button>

          </div>

        </div>

        {/* =================================================
            FORM
        ================================================= */}

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >

          {/* =================================================
              FORM CONTENT
          ================================================= */}

          <div className="min-h-0 flex-1 overflow-y-auto p-6">

            {error && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-5">

              {/* =================================================
                  WORKSPACE CODE
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Code
                </label>

                <input
                  name="workspace_code"
                  value={
                    formData.workspace_code
                  }
                  onChange={handleChange}
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />

              </div>

              {/* =================================================
                  WORKSPACE NAME
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Name
                </label>

                <input
                  name="workspace_name"
                  value={
                    formData.workspace_name
                  }
                  onChange={handleChange}
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />

              </div>

              {/* =================================================
                  EXECUTION MODE
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Execution Mode
                </label>

                <select
                  name="execution_mode"
                  value={
                    formData.execution_mode
                  }
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >

                  <option value="Work">
                    Work
                  </option>

                  <option value="Pipeline">
                    Pipeline
                  </option>

                  <option value="Hybrid">
                    Hybrid
                  </option>

                </select>

              </div>

              {/* =================================================
                  WORKSPACE LEAD
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Lead
                </label>

                <select
                  name="workspace_lead_employee_uuid"
                  value={
                    formData.workspace_lead_employee_uuid
                  }
                  onChange={handleChange}
                  disabled={loadingEmployees}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                >

                  <option value="">
                    {loadingEmployees
                      ? "Loading employees..."
                      : "Select workspace lead"}
                  </option>

                  {employees.map(
                    (employee, index) => {

                      /*
                       * Handle different backend
                       * employee UUID field names.
                       */
                      const employeeUuid =
                        employee.uuid ||
                        employee.employee_uuid ||
                        employee.employeeUuid ||
                        employee.id;

                      /*
                       * Handle different employee
                       * name field names.
                       */
                      const employeeName =
                        employee.name ||
                        employee.employee_name ||
                        employee.employeeName ||
                        employee.full_name ||
                        employee.fullName ||
                        `${employee.first_name || ""} ${
                          employee.last_name || ""
                        }`.trim() ||
                        `Employee ${index + 1}`;

                      return (
                        <option
                          key={
                            employeeUuid ||
                            index
                          }
                          value={
                            employeeUuid
                          }
                        >
                          {employeeName}
                        </option>
                      );
                    }
                  )}

                </select>

                {loadingEmployees && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">

                    <Loader2
                      size={14}
                      className="animate-spin"
                    />

                    Loading employees...

                  </div>
                )}

              </div>

              {/* =================================================
                  HOURS + DISPLAY ORDER
              ================================================= */}

              <div className="grid grid-cols-2 gap-4">

                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Estimated Hours
                  </label>

                  <input
                    type="number"
                    name="estimated_hours"
                    value={
                      formData.estimated_hours
                    }
                    onChange={handleChange}
                    min="0"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />

                </div>

                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Display Order
                  </label>

                  <input
                    type="number"
                    name="display_order"
                    value={
                      formData.display_order
                    }
                    onChange={handleChange}
                    min="1"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />

                </div>

              </div>

              {/* =================================================
                  STATUS
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Status
                </label>

                <select
                  name="status"
                  value={
                    formData.status
                  }
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >

                  <option value="Planning">
                    Planning
                  </option>

                  <option value="Active">
                    Active
                  </option>

                  <option value="Completed">
                    Completed
                  </option>

                  <option value="On Hold">
                    On Hold
                  </option>

                </select>

              </div>

              {/* =================================================
                  DESCRIPTION
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Description
                </label>

                <textarea
                  name="description"
                  value={
                    formData.description
                  }
                  onChange={handleChange}
                  rows={5}
                  className="mt-2 w-full resize-none rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />

              </div>

            </div>

          </div>

          {/* =================================================
              FOOTER
          ================================================= */}

          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4">

            <div className="flex justify-end gap-3">

              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >

                {saving && (
                  <Loader2
                    size={16}
                    className="animate-spin"
                  />
                )}

                {saving
                  ? "Saving..."
                  : "Save Changes"}

              </button>

            </div>

          </div>

        </form>

      </div>
    </>
  );
};

export default EditWorkspaceDrawer;